<!-- src/components/PeriodsPricesDialog.vue -->
<template>
  <v-dialog v-model="isOpen" max-width="1280" eager @keydown.esc="close">
    <v-card class="dlg-card">
      <v-progress-linear v-if="saving" indeterminate absolute top />

      <!-- Header -->
      <div class="dlg-header">
        <div class="dlg-title">
          <v-icon left>mdi-timetable</v-icon>
          {{ $t('Periods') }} — {{ periodItem?.names?.uz || periodItem?.mvdir }}
        </div>
        <v-btn icon :disabled="saving" @click="close" aria-label="Close">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </div>

      <!-- Body: main + sidebar -->
      <div class="dlg-body">
        <!-- Left: main content -->
        <div class="main">
          <div class="dlg-section">
            <div class="dlg-subtitle">
              {{ $t('Prices') }} — {{ periodItem?.mvdir }}
              <span v-if="typeFilter !== 'all' || languageFilter !== 'all' || selectedCourse !== null || selectedYear" class="active-filters">
                •
                <span v-if="selectedYear">{{ selectedYear }}</span>
                <span v-if="typeFilter !== 'all'">, {{ typeLabelById(typeFilter) }}</span>
                <span v-if="languageFilter !== 'all'">, {{ languageLabelById(languageFilter) }}</span>
                <span v-if="selectedCourse !== null">, {{ $t('Course') }} {{ selectedCourse }}</span>
              </span>
            </div>

            <div v-if="loading" class="prices-loading">
              <div class="sk sk-line" style="width: 40%"></div>
              <div class="sk sk-line" style="width: 75%"></div>
              <div class="sk sk-line" style="width: 60%"></div>
            </div>

            <v-alert v-else-if="loadError" type="error" dense outlined>
              {{ loadError }}
            </v-alert>

            <v-alert v-else-if="!editablePrices.length" type="info" dense outlined>
              {{ $t('No prices found for this faculty.') }}
            </v-alert>

            <template v-else>
              <v-simple-table class="prices-table editable-table">
                <thead>
                <tr>
                  <th style="width:150px">{{ $t('Type') }}</th>
                  <th style="width:124px">{{ $t('Education Language') }}</th>
                  <th style="width:76px">{{ $t('Srok') }}</th>
                  <th style="width:72px">{{ $t('Course') }}</th>
                  <th style="width:150px" class="text-right">{{ $t('Paid Summ') }}</th>
                  <th style="width:130px">{{ $t('Codes') }}</th>
                  <th style="width:56px" class="text-right">{{ $t('Actions') }}</th>
                </tr>
                </thead>
                <tbody>
                <tr v-for="p in pricesView" :key="p._id">
                  <!-- Type -->
                  <td>
                    <v-select
                        :items="priceTypes"
                        item-text="label"
                        item-value="value"
                        dense outlined hide-details
                        v-model="p.type"
                        :disabled="saving || deleting"
                        :menu-props="{ offsetY: true, bottom: true, nudgeBottom: 4, maxHeight: 300, contentClass: 'period-type-menu' }"
                        @change="touch"
                    />
                  </td>

                  <!-- Education language. Not clearable: a row always belongs to one language, so
                       there is nothing to clear it back to. -->
                  <td>
                    <v-select
                        :items="languageSelectItems"
                        item-text="label"
                        item-value="value"
                        dense outlined hide-details
                        v-model="p.education_language_id"
                        :disabled="saving || deleting"
                        :menu-props="{ offsetY: true, bottom: true, nudgeBottom: 4, maxHeight: 300, contentClass: 'period-type-menu' }"
                        @change="touch"
                    />
                  </td>

                  <!-- Editable srok_n (nullable) -->
                  <td>
                    <v-text-field
                        :value="p.srok_n == null ? '' : p.srok_n"
                        type="number" min="0" step="0.5"
                        dense outlined hide-details class="mono"
                        :disabled="saving || deleting"
                        :rules="[v => validateFloatOrEmpty(v)]"
                        placeholder="2"
                        @input="onSrokInput(p, $event)"
                        @blur="onSrokBlur(p)"
                    />
                  </td>

                  <!-- Kurs -->
                  <td>
                    <v-text-field
                        v-model.number="p.kurs"
                        type="number" min="0" step="1"
                        dense outlined hide-details class="mono"
                        :disabled="saving || deleting"
                        :rules="[v => validateIntOrEmpty(v)]"
                        placeholder="1"
                        @change="touch"
                    />
                  </td>

                  <!-- summ_n -->
                  <td class="text-right">
                    <v-text-field
                        :value="formatCurrency(p.summ_n)"
                        dense outlined hide-details class="mono text-right"
                        :disabled="saving || deleting"
                        :rules="[() => validateAmountNumber(p.summ_n)]"
                        placeholder="12 345 000"
                        @focus="selectAll($event)"
                        @input="onSummInput(p, $event)"
                        @blur="onSummBlur(p)"
                    />
                  </td>

                  <!-- Codes -->
                  <td>
                    <v-text-field
                        v-model="p.faculty_code"
                        dense outlined hide-details
                        :disabled="saving || deleting"
                        placeholder="6051041-..."
                        @change="touch"
                    />
                  </td>

                  <!-- Actions -->
                  <td class="text-right">
                    <v-btn
                        icon small
                        :disabled="saving || deleting"
                        @click.stop="onDeleteClick(p)"
                        :title="$t('Delete')"
                    >
                      <v-icon>mdi-trash-can-outline</v-icon>
                    </v-btn>
                  </td>
                </tr>
                </tbody>
              </v-simple-table>
            </template>

            <!-- Add new row button (visible always) -->
            <div style="margin-top: 10px; display: flex; gap: 8px; align-items: center;">
              <v-btn class="bordered-btn" small text :disabled="saving || deleting" @click="addNewRow">
                <v-icon left small>mdi-plus</v-icon>
                {{ $t('Add new price') }}
              </v-btn>
              <v-btn
                v-if="selectedYear === yearOptions[0] && !editablePrices.length"
                class="copy-prev-btn"
                small
                :loading="copying"
                :disabled="saving || deleting || copying"
                @click="copyFromPreviousYear"
              >
                <v-icon left small>mdi-auto-fix</v-icon>
                O'tgan yildan ko'chirish
              </v-btn>
            </div>
          </div>
        </div>

        <!-- Right: sidebar -->
        <aside class="sidebar">
          <!-- Academic year -->
          <div class="sb-section">
            <div class="sb-title">{{ $t('Academic year') }}</div>
            <div class="seg seg--col">
              <button
                  v-for="y in yearOptions"
                  :key="y"
                  class="seg__btn seg__btn--col"
                  :class="{ 'seg__btn--active': selectedYear === y }"
                  @click="selectYear(y)"
              >
                {{ y }}
              </button>
            </div>
          </div>

          <!-- Mode -->
          <div class="sb-section">
            <div class="sb-title">{{ $t('Education Type') }}</div>
            <div class="sidebar-chips">
              <v-chip
                  class="chip-block"
                  :class="{ 'type-chip--active': typeFilter==='all' }"
                  pill
                  @click="setType('all')"
              >{{ $t('All') }}</v-chip>
              <v-chip
                  class="chip-block"
                  :class="{ 'type-chip--active': Number(typeFilter)===1 }"
                  pill
                  @click="setType(1)"
              >{{ $t('Kunduzgi') }}</v-chip>
              <v-chip
                  class="chip-block"
                  :class="{ 'type-chip--active': Number(typeFilter)===2 }"
                  pill
                  @click="setType(2)"
              >{{ $t('Kechki') }}</v-chip>
              <v-chip
                  class="chip-block"
                  :class="{ 'type-chip--active': Number(typeFilter)===3 }"
                  pill
                  @click="setType(3)"
              >{{ $t('Sirtqi') }}</v-chip>
              <v-chip
                  class="chip-block"
                  :class="{ 'type-chip--active': Number(typeFilter)===4 }"
                  pill
                  @click="setType(4)"
              >{{ $t('Kunduzgi 3-smena') }}</v-chip>
            </div>
          </div>

          <!-- Education language -->
          <div class="sb-section">
            <div class="sb-title">{{ $t('Education Language') }}</div>
            <div class="sidebar-chips">
              <v-chip
                  class="chip-block"
                  :class="{ 'type-chip--active': languageFilter==='all' }"
                  pill
                  @click="setLanguage('all')"
              >{{ $t('All') }}</v-chip>
              <v-chip
                  v-for="l in languageOptions"
                  :key="'lang' + l.value"
                  class="chip-block"
                  :class="{ 'type-chip--active': Number(languageFilter)===l.value }"
                  pill
                  @click="setLanguage(l.value)"
              >{{ l.label }}</v-chip>
            </div>
          </div>

          <!-- Course -->
          <div class="sb-section">
            <div class="sb-title">{{ $t('Course') }}</div>
            <div class="seg seg--col">
              <button
                  v-for="c in [1,2,3,4,5]"
                  :key="'c' + c"
                  class="seg__btn seg__btn--col"
                  :class="{ 'seg__btn--active': selectedCourse === c }"
                  @click="selectCourse(c)"
              >{{ c }}</button>
              <button
                  class="seg__btn seg__btn--col seg__btn--ghost"
                  :class="{ 'seg__btn--active': selectedCourse === null }"
                  @click="clearCourseFilter()"
              >{{ $t('All') }}</button>
            </div>
          </div>

          <div class="sb-section">
            <v-btn small text :disabled="saving || deleting" @click="resetFilters">{{ $t('Reset filters') }}</v-btn>
          </div>
        </aside>
      </div>

      <!-- Delete confirm dialog -->
      <v-dialog v-model="deleteDialog" max-width="420">
        <v-card>
          <v-card-title class="headline">{{ $t('Delete price row?') }}</v-card-title>
          <v-card-text>
            {{ $t('This action cannot be undone.') }}
            <div v-if="deleteTarget && deleteTarget.id" style="margin-top:6px; font-size:12.5px; color:#64748b">
              ID: {{ deleteTarget.id }} · {{ typeLabelById(deleteTarget.type) }}
              <span v-if="deleteTarget.kurs"> · {{ $t('Course') }} {{ deleteTarget.kurs }}</span>
            </div>
          </v-card-text>
          <v-card-actions>
            <v-spacer />
            <v-btn text :disabled="deleting" @click="deleteDialog=false">{{ $t('Cancel') }}</v-btn>
            <v-btn color="red darken-1" dark :loading="deleting" :disabled="deleting" @click="confirmDelete">
              {{ $t('Delete') }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Sticky actions -->
      <div class="dlg-actions sticky-actions">
        <div class="left">
          <v-alert v-if="saveError" type="error" dense outlined class="mr-3">{{ saveError }}</v-alert>
          <v-alert v-if="saveSuccess" type="success" dense outlined class="mr-3">{{ $t('Saved successfully') }}</v-alert>
        </div>
        <div class="right" style="display: flex; gap: 8px;">
          <v-btn text :disabled="saving || deleting" @click="close">
            {{ $t('Cancel') }}
          </v-btn>
          <v-btn class="save-btn" :loading="saving" :disabled="!dirty || deleting" @click="save">
            {{ $t('Save') }}
          </v-btn>
        </div>
      </div>
    </v-card>
  </v-dialog>
</template>

<script>
import axios from 'axios';

export default {
  name: 'PeriodsPricesDialog',
  props: {
    periodDialog: { type: Boolean, required: true },
    periodItem: { type: Object, default: null },
  },
  data: () => ({
    loading: false,
    loadError: null,

    // Data
    editablePrices: [],   // [{ _id, id, type, srok_n, kurs, summ_n, faculty_code, academic_year? }]
    pricesView: [],

    // Filters / UI state
    typeFilter: 'all',
    languageFilter: 'all',
    selectedCourse: null,
    yearOptions: ['2026-2027','2025-2026','2024-2025','2023-2024','2022-2023'],
    selectedYear: '2026-2027',
    priceTypes: [
      { value: 1, label: 'Kunduzgi' },
      { value: 2, label: 'Kechki' },
      { value: 3, label: 'Sirtqi' },
      { value: 4, label: "Kunduzgi ta'lim 3-smena (18:00-21:20)" },
    ],
    // education_language ids, matching the backend's O'zbek=1 / Rus=2 / Ingliz=3.
    // Ingliz is listed so a row can be priced by hand once those prices exist — the split does not
    // generate Ingliz rows, because inventing prices nobody has set would put guesses on contracts.
    languageOptions: [
      { value: 1, label: "O'zbek" },
      { value: 2, label: 'Rus' },
      { value: 3, label: 'Ingliz' },
    ],
    /** What a brand-new row gets when no language filter narrows the choice. */
    defaultLanguageId: 1,

    // Save/dirty
    saving: false,
    saveError: null,
    saveSuccess: false,
    dirty: false,

    // Copy from previous year
    copying: false,

    // Delete
    deleteDialog: false,
    deleteTarget: null,
    deleting: false,
  }),
  computed: {
    isOpen: {
      get() { return !!this.periodDialog; },
      set(v) { this.$emit('update:periodDialog', !!v); },
    },
    facultyId() {
      return (
          this.periodItem?.education_faculty_id ||
          this.periodItem?.educationFacultyId ||
          this.periodItem?.id ||
          null
      );
    },
    /**
     * Row dropdown items — the concrete languages only. There is deliberately no "Barchasi" entry:
     * every price row belongs to exactly one language, so a row that still shows blank is one the
     * language split has not reached yet, not a row that means "all languages".
     * ("Barchasi" remains a SIDEBAR FILTER, where it means "show every row".)
     */
    languageSelectItems() {
      return this.languageOptions;
    },
    mvdir() { return this.periodItem?.mvdir || null; },
  },
  watch: {
    isOpen(v) { v ? this.reload() : this.resetState(); },
    periodItem(n, o) { if (this.isOpen && n && n.id !== o?.id) this.reload(); },
    typeFilter() { this.recomputePricesView(); },
    languageFilter() { this.recomputePricesView(); },
    selectedCourse() { this.recomputePricesView(); },
    async selectedYear() { this._keepFilters = true; await this.reload(); },
    editablePrices: { deep: true, handler() { this.recomputePricesView(); this.dirty = true; } },
  },
  methods: {
    http() {
      const a = this.$axios || axios;
      if (a.create) return a;
      if (a.get && typeof a.get === 'function') return a;
      return axios;
    },

    async reload() {
      this.loading = true;
      this.loadError = null;
      this.saveError = null;
      this.saveSuccess = false;
      this.dirty = false;

      try {
        if (!this.facultyId) throw new Error('No faculty id or mvdir');

        const { data } = await this.http().get(
            `/admin/faculty/get/${this.facultyId}/prices`,
            { params: { studyYear: this.selectedYear || this.yearOptions[0] } }
        );

        const arr = Array.isArray(data) ? data : (Array.isArray(data?.prices) ? data.prices : []);
        this.editablePrices = (arr || []).map((p, i) => ({
          _id: p.id || `row-${i}-${Date.now()}-${Math.random().toString(36).slice(2,7)}`,
          id: p.id ?? null,
          type: (p.type ?? p.education_type_id) || 1,
          srok_n: (p.srok_n === 0 || p.srok_n) ? Number(p.srok_n) : null,
          kurs: (p.kurs === 0 || p.kurs) ? Number(p.kurs) : null,
          summ_n: this.parseCurrencyToNumber(p.summ_n) ?? 0,
          faculty_code: (p.faculty_code ?? '').toString(),
          // null stays null: it means "applies to every language", not "unset".
          education_language_id: (p.education_language_id === 0 || p.education_language_id)
              ? Number(p.education_language_id) : null,
          academic_year: p.academic_year || p.year || null,
        }));

        // Reset filters only on first open, not on year switch
        if (!this._keepFilters) {
          this.typeFilter = 'all';
          this.selectedCourse = null;
        }
        this._keepFilters = false;

        this.recomputePricesView();
        this.dirty = false;
      } catch (e) {
        this.loadError = e?.response?.data?.message || e?.message || 'Load error';
        // eslint-disable-next-line no-console
        console.error('Load error:', e);
      } finally {
        this.loading = false;
      }
    },

    resetState() {
      this.loading = false;
      this.loadError = null;
      this.editablePrices = [];
      this.pricesView = [];
      this.typeFilter = 'all';
      this.languageFilter = 'all';
      this.selectedCourse = null;
      this.saving = false;
      this.saveError = null;
      this.saveSuccess = false;
      this.dirty = false;
      this.deleteDialog = false;
      this.deleteTarget = null;
      this.deleting = false;
    },

    // ===== Sidebar interactions =====
    setType(v) { this.typeFilter = v; },
    setLanguage(v) { this.languageFilter = v; },
    selectCourse(c) { this.selectedCourse = Number(c); },
    clearCourseFilter() { this.selectedCourse = null; },
    resetFilters() { this.typeFilter = 'all'; this.languageFilter = 'all'; this.selectedCourse = null; },
    selectYear(y) { this.selectedYear = y; },

    // ===== Filtering =====
    recomputePricesView() {
      let arr = this.editablePrices.slice();

      // Year if present in rows
      if (this.selectedYear) {
        const anyHasYear = arr.some(p => p.academic_year);
        if (anyHasYear) {
          arr = arr.filter(p => !p.academic_year || p.academic_year === this.selectedYear);
        }
      }

      // Mode
      if (this.typeFilter !== 'all') {
        const wanted = Number(this.typeFilter);
        arr = arr.filter(p => Number(p.type) === wanted);
      }

      // Education language: a row with no language applies to every language, so it stays visible
      // under any selection — that is how the study years that were never split still show up.
      if (this.languageFilter !== 'all') {
        const wantedLang = Number(this.languageFilter);
        arr = arr.filter(p => p.education_language_id == null
            || Number(p.education_language_id) === wantedLang);
      }

      // Course: include base (null) together with selected
      if (this.selectedCourse !== null) {
        const c = Number(this.selectedCourse);
        arr = arr.filter(p => (p.kurs === null || Number(p.kurs) === c));
      }

      this.pricesView = arr;
    },

    // ===== Helpers =====
    languageLabelById(v) {
      const found = this.languageOptions.find(l => l.value === Number(v));
      return found ? found.label : '';
    },

    typeLabelById(v) {
      const id = Number(v);
      if (id === 1) return this.$t('Kunduzgi');
      if (id === 2) return this.$t('Kechki');
      if (id === 3) return this.$t('Sirtqi');
      if (id === 4) return this.$t('Kunduzgi 3-smena');
      return this.$t('All');
    },

    // ===== Validation =====
    validateInt(v) {
      const n = Number(v);
      return (Number.isInteger(n) && n >= 0) || this.$t('Enter a non-negative integer');
    },
    validateIntOrEmpty(v) {
      if (v === '' || v === null || v === undefined) return true;
      return this.validateInt(v);
    },
    validateFloatOrEmpty(v) {
      if (v === '' || v === null || v === undefined) return true;
      const n = Number(v);
      return (isFinite(n) && n >= 0) || this.$t('Enter a non-negative number');
    },
    validateAmountNumber(n) {
      return (typeof n === 'number' && isFinite(n) && n >= 0) || this.$t('Enter a valid amount');
    },

    // ===== Currency =====
    parseCurrencyToNumber(v) {
      if (v == null) return null;
      let s = String(v).trim();
      if (!s) return null;
      s = s.replace(/[\u00A0\s]/g, '').replace(/[^0-9,.]/g, '');
      const lastComma = s.lastIndexOf(',');
      const lastDot = s.lastIndexOf('.');
      if (lastComma >= 0 && lastDot >= 0) {
        if (lastComma > lastDot) s = s.replace(/\./g, '').replace(',', '.');
        else s = s.replace(/,/g, '');
      } else if (lastComma >= 0) {
        s = s.replace(',', '.');
      }
      const num = Number(s);
      return Number.isFinite(num) ? num : null;
    },
    formatCurrency(n) {
      if (n == null || n === '') return '';
      if (typeof n !== 'number' || !isFinite(n)) return '';
      return Math.trunc(n).toLocaleString('ru-RU').replace(/,/g, ' ');
    },

    // ===== Input UX =====
    selectAll(evt) {
      const el = evt?.target;
      if (el?.select) requestAnimationFrame(() => el.select());
    },
    onSummInput(row, evt) {
      const val = typeof evt === 'string' ? evt : evt?.target?.value;
      const num = this.parseCurrencyToNumber(val);
      row.summ_n = num ?? 0;
      this.touch();
    },
    onSummBlur(row) {
      if (row.summ_n == null || !isFinite(row.summ_n)) row.summ_n = 0;
    },
    onSrokInput(row, evt) {
      const val = typeof evt === 'string' ? evt : evt?.target?.value;
      if (val === '' || val == null) {
        row.srok_n = null;
      } else {
        const num = Number(val);
        row.srok_n = isFinite(num) && num >= 0 ? num : row.srok_n;
      }
      this.touch();
    },
    onSrokBlur(row) {
      if (row.srok_n === '' || row.srok_n === undefined) row.srok_n = null;
      if (row.srok_n != null && !isFinite(Number(row.srok_n))) row.srok_n = null;
    },

    // ===== Create support =====
    // replace your addNewRow() with this
    addNewRow() {
      const course = this.selectedCourse === null ? null : Number(this.selectedCourse);
      const type = this.typeFilter === 'all' ? 1 : Number(this.typeFilter);

      const row = {
        _id: `new-${Date.now()}-${Math.random().toString(36).slice(2,7)}`,
        id: null,
        type,                       // respect current Mode filter
        srok_n: null,
        kurs: course,               // respect current Course filter (null = base row)
        summ_n: 0,
        faculty_code: '',
        // Respect the current language filter, the same way type/course are respected above.
        // With no language filter active there is still no such thing as a language-less row, so a
        // new one starts as O'zbek rather than blank.
        education_language_id: this.languageFilter === 'all'
            ? this.defaultLanguageId
            : Number(this.languageFilter),
        academic_year: this.selectedYear,
      };

      this.editablePrices.push(row);
      this.recomputePricesView();
      this.dirty = true;

      // optional: auto-scroll to the bottom so the new row is visible
      this.$nextTick(() => {
        const tbody = this.$el.querySelector('.prices-table tbody');
        if (tbody) tbody.scrollTop = tbody.scrollHeight;
      });
    },

    // ===== Copy from previous year =====
    async copyFromPreviousYear() {
      this.copying = true;
      try {
        await this.http().post(
          `/admin/faculty/copy-prices-from-previous-year`,
          null,
          { params: { educationFacultyId: this.periodItem.id, targetYear: this.selectedYear } }
        );
        await this.reload();
      } catch (e) {
        const msg = e?.response?.data?.message || e?.message || 'Failed to copy';
        this.saveError = msg;
        setTimeout(() => { this.saveError = null; }, 4000);
      } finally {
        this.copying = false;
      }
    },

    // ===== Delete =====
    onDeleteClick(row) {
      // Unsaved row: remove instantly
      if (!row.id) {
        const idx = this.editablePrices.findIndex(x => x._id === row._id);
        if (idx !== -1) this.editablePrices.splice(idx, 1);
        this.recomputePricesView();
        this.touch();
        return;
      }
      // Saved row: confirm
      this.deleteTarget = row;
      this.deleteDialog = true;
    },

    async confirmDelete() {
      if (!this.deleteTarget) return;
      const target = this.deleteTarget;

      // Optimistic remove with rollback snapshot
      const snapshot = this.editablePrices.slice();
      this.deleting = true;

      this.editablePrices = this.editablePrices.filter(x => x._id !== target._id);
      this.recomputePricesView();
      this.touch();

      try {
        await this.http().delete(`/admin/faculty/delete/price/${target.id}`);
        // Optional: show success banner
        this.$nextTick(() => { this.saveSuccess = true; });
      } catch (e) {
        // Rollback
        this.editablePrices = snapshot;
        this.recomputePricesView();
        this.saveError = e?.response?.data?.message || e?.message || this.$t('Delete error');
        // eslint-disable-next-line no-console
        console.error('Delete error:', e);
      } finally {
        this.deleting = false;
        this.deleteDialog = false;
        this.deleteTarget = null;
      }
    },

    // ===== Save =====
    touch() { this.dirty = true; },
    close() { this.isOpen = false; },

    async save() {
      try {
        if (!this.facultyId) throw new Error('No faculty id or mvdir');

        this.saving = true;
        this.saveError = null;
        this.saveSuccess = false;

        const reqs = this.editablePrices.map(p => {
          const dto = {
            education_faculty_id: this.facultyId,
            education_type_id: Number(p.type) || null,
            srok_n: (p.srok_n === '' || p.srok_n === undefined) ? null : (p.srok_n == null ? null : Number(p.srok_n)),
            kurs: (p.kurs === '' || p.kurs === null || p.kurs === undefined) ? null : Number(p.kurs),
            summ_n: String(Math.trunc(Number(p.summ_n) || 0)),
            faculty_code: (p.faculty_code ?? '').toString().trim() || null,
            education_language_id: (p.education_language_id === 0 || p.education_language_id)
                ? Number(p.education_language_id) : null,
            active: true,
          };

          if (p.id) {
            return this.http().put(`/admin/faculty/edit/price/${p.id}`, dto);
          } else {
            return this.http().post(`/admin/faculty/create/price?studyYear=` + this.selectedYear, dto);
          }
        });

        const results = await Promise.allSettled(reqs);
        const failures = results.filter(r => r.status === 'rejected');

        if (failures.length) {
          const msgs = failures.map(r => r.reason?.response?.data?.message || r.reason?.message || 'Row failed');
          throw new Error(msgs.join('; '));
        }

        this.saveSuccess = true;
        this.dirty = false;

        await this.reload();
        this.$emit('saved');
      } catch (e) {
        this.saveError = e?.response?.data?.message || e?.message || 'Save error';
        // eslint-disable-next-line no-console
        console.error('Save error:', e);
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style scoped>
/* Layout */
.dlg-card { background: #f5f5f5; border:1px solid rgba(0,30,60,.08); border-radius:16px; }
.dlg-header { display:flex; align-items:center; justify-content:space-between; padding:14px 18px; border-bottom:1px solid rgba(0,30,60,.08); }
.dlg-title { font-weight:700; display:flex; gap:8px; align-items:center; }

.dlg-body { display:flex; gap:16px; padding: 10px 12px 0 12px; }
.main { flex: 1 1 auto; min-width: 0; }
/* Narrower than before: the width goes to the table, which now carries a seventh column. The
   sidebar scrolls on its own because adding "Ta'lim tili" pushed its last chips past the dialog
   edge on shorter screens — previously the whole panel just got clipped. */
.sidebar {
  flex: 0 0 232px;
  display:flex; flex-direction:column; gap:12px;
  max-height: 72vh;
  overflow-y: auto;
  padding-right: 2px;
}

/* Sections */
.dlg-subtitle { padding:10px 6px 0 6px; color:#64748b; font-size:13px; }
.dlg-table { margin:10px 6px 0 6px; border:1px solid rgba(0,30,60,.08); border-radius:12px; overflow:hidden; }
.dlg-section { padding:10px 6px 0 6px; }
.prices-table { margin-top:8px; border:1px solid rgba(0,30,60,.08); border-radius:12px; overflow:hidden; }

.sb-section { background: rgba(255,255,255,.85); border:1px solid rgba(0,30,60,.08); border-radius:12px; padding:10px; }
.sb-title { font-weight:700; font-size:12.5px; color:#334155; margin: 0 0 8px; }

/* Sidebar chips vertical */
.sidebar-chips { display:flex; flex-direction:column; gap:6px; }
.chip-block { width: 100%; justify-content: center; }
.v-chip.type-chip--active,
.chip-block.type-chip--active {
  background:#fde047 !important;
  color:#111827 !important;
  box-shadow:0 6px 18px rgba(245,158,11,.22);
  border-color:rgba(0,30,60,.12);
}

.save-btn {
  background:#fde047 !important;
  color:#111827 !important;
  box-shadow:0 6px 18px rgba(245,158,11,.22);
  border-color:rgba(0,30,60,.12);
  padding:10px 15px;
}

/* Segmented controls */
.seg {
  display:flex; align-items:center; gap:6px;
  background:#eef2f7;
  border:1px solid rgba(0,30,60,.08);
  border-radius:999px;
  padding:6px;
  box-shadow:0 4px 12px rgba(2,6,23,.05);
  flex-wrap: wrap;
}
.seg--col { flex-direction:column; align-items:stretch; }
.seg__btn{
  appearance:none; border:0; background:transparent; color:#475569;
  font-weight:700; letter-spacing:.3px; font-size:13.5px; padding:8px 14px;
  border-radius:999px; cursor:pointer; min-width: 100px; text-align:center;
  transition: transform .12s ease, background-color .15s ease, box-shadow .15s ease, color .15s ease, border-color .15s ease;
  -webkit-tap-highlight-color: transparent; user-select:none;
}
.seg__btn--col { width: 100%; }
.seg__btn--ghost { opacity:.9; }
.seg__btn--active{
  background:#fde047; color:#111827;
  box-shadow:0 6px 18px rgba(245,158,11,.22);
  border:1px solid rgba(0,30,60,.12);
}
.seg__btn:active{ transform: translateY(.5px) scale(.99); }

/* Helpers & loading */
.active-filters { color:#64748b; font-size:12.5px; }
.mono { font-variant-numeric: tabular-nums; }
.num-input .v-input__slot { justify-content:flex-end; }
.prices-loading .sk-line {
  height:12px; border-radius:8px; margin:8px 0;
  background:linear-gradient(100deg,#eef2f7 20%,#f6f9ff 40%,#eef2f7 60%);
  background-size:200% 100%; animation: shimmer 1.2s ease-in-out infinite;
}

/* Compact outlined inputs inside the editable table */
.editable-table ::v-deep .v-input--outlined > .v-input__control > .v-input__slot {
  min-height: 38px !important;
  padding: 0 10px !important;
}
.editable-table ::v-deep .v-input--outlined.v-input--dense .v-select__selections,
.editable-table ::v-deep .v-input--outlined.v-input--dense input {
  padding-top: 0 !important;
  padding-bottom: 0 !important;
  font-size: 13.5px;
}
.editable-table ::v-deep .v-input--outlined .v-input__append-inner {
  margin-top: 7px !important;
}
.editable-table ::v-deep .v-text-field--outlined fieldset {
  border-color: #e2e8f0 !important;
}
.editable-table ::v-deep td {
  padding: 8px 6px !important;
}
.editable-table ::v-deep th {
  padding: 0 6px !important;
  white-space: nowrap;
}

/* The language column pushed the row past what 7 columns fit in comfortably, and the flexible
   money/code columns were the ones that paid for it — amounts rendered as "18 48" and codes as
   "24-I". Give the table a floor and let its own wrapper scroll instead of letting cells crush:
   a horizontal scrollbar is recoverable, a silently truncated price is not. */
.editable-table ::v-deep table {
  min-width: 860px;
}
.editable-table ::v-deep .v-data-table__wrapper {
  overflow-x: auto;
}

/* Amounts and codes must never wrap mid-value — reading "18 48" as a price is a real mistake. */
.editable-table ::v-deep td:nth-child(5) input,
.editable-table ::v-deep td:nth-child(6) input {
  white-space: nowrap;
  text-overflow: ellipsis;
}

/* Selects carry a dropdown arrow, so they need less side padding than free-text fields to keep
   "Kunduzgi ta'lim 3-smena (18:00-21:20)" legible in a 150px column. */
.editable-table ::v-deep .v-select .v-input__slot {
  padding: 0 4px 0 8px !important;
}

/* Sticky footer actions */
.sticky-actions {
  position: sticky; bottom: 0; display:flex; justify-content:space-between; align-items:center;
  padding:10px 16px; background:rgba(250,252,255,.85); backdrop-filter:blur(6px);
  border-top:1px solid rgba(0,30,60,.08);
}

/* Tiny icon polish */
.v-btn .v-icon { opacity:.9; }

@keyframes shimmer { 0%{background-position:200% 0} 100%{background-position:-200% 0} }

.bordered-btn {
  background-color: transparent !important;
  border: 1px solid #4f46e5 !important;
  color: #4f46e5 !important;
  transition: 0.2s ease;
}
.bordered-btn:hover {
  background-color: rgba(33, 150, 243, 0.08) !important; /* light hover effect */
}
.bordered-btn:disabled {
  opacity: 0.5;
  border-color: #ccc !important;
  color: #aaa !important;
}

.copy-prev-btn {
  background-color: #7c3aed !important;
  color: #fff !important;
  font-weight: 600 !important;
  text-transform: none !important;
  border-radius: 8px !important;
  letter-spacing: 0.02em;
}
.copy-prev-btn:hover {
  background-color: #6d28d9 !important;
}
.copy-prev-btn.v-btn--disabled {
  background-color: #7c3aed !important;
  color: #fff !important;
  opacity: 0.45;
}

</style>

<style>
/* Not scoped: v-menu__content teleports to the app root, so scoped styles
   wouldn't reach it. */
.period-type-menu.v-menu__content {
  min-width: 180px !important;
  border-radius: 10px !important;
  box-shadow: 0 12px 32px rgba(2, 6, 23, .14), 0 2px 6px rgba(2, 6, 23, .08) !important;
  border: 1px solid rgba(0, 30, 60, .08);
  background: #fff;
}
.period-type-menu .v-list {
  padding: 6px !important;
}
.period-type-menu .v-list-item {
  min-height: 38px !important;
  font-size: 13.5px;
  border-radius: 8px !important;
}
.period-type-menu .v-list-item--active {
  background: #fef3c7 !important;
  color: #111827 !important;
  font-weight: 600;
}
.period-type-menu .v-list-item:hover {
  background: #f8fafc;
}
.period-type-menu .v-list-item {
  cursor: pointer;
}
/* The menu is a picker, so dragging across it should not start a text selection. */
.period-type-menu .v-list-item__title,
.period-type-menu .v-list-item__content {
  user-select: none;
}
/* Long option labels ("Kunduzgi ta'lim 3-smena (18:00-21:20)") must stay readable rather than being
   cut by the field's width — the menu is free to be wider than the column it belongs to. */
.period-type-menu .v-list-item__title {
  white-space: nowrap;
  overflow: visible;
}
.period-type-menu {
  min-width: max-content !important;
}
</style>

<!-- Not scoped: v-select renders its own markup, and the menu is teleported to the app root, so
     scoped selectors would never reach either. -->
<style>
/* A select is a picker, not a text field: the whole control should say "click me". Vuetify leaves an
   I-beam over the input, which reads as editable text and makes people click the small arrow instead
   of the field. */
.editable-table .v-select,
.editable-table .v-select .v-input__slot,
.editable-table .v-select .v-select__selections,
.editable-table .v-select input,
.editable-table .v-select .v-input__append-inner,
.editable-table .v-select .v-input__append-inner * {
  cursor: pointer !important;
}
/* No blinking caret, and no accidental text-drag when the intent was to open the menu. */
.editable-table .v-select input {
  caret-color: transparent !important;
  user-select: none;
}
.editable-table .v-select .v-select__selection {
  user-select: none;
}
/* Make the whole field feel live, so the target is the row-height control rather than the arrow. */
.editable-table .v-select:hover .v-input__slot {
  background: #f8fafc;
}
.editable-table .v-select:hover fieldset {
  border-color: #cbd5e1 !important;
}
/* The arrow is the affordance — keep it from being a faint 12px hint. */
.editable-table .v-select .v-input__append-inner .v-icon {
  font-size: 20px;
  opacity: .75;
}
.editable-table .v-select:hover .v-input__append-inner .v-icon {
  opacity: 1;
}

/* Everything else in the dialog that responds to a click should say so too. */
.dlg-card .chip-block,
.dlg-card .seg__btn,
.dlg-card .v-chip,
.dlg-card .prices-table .v-icon,
.dlg-card .v-btn:not(.v-btn--disabled) {
  cursor: pointer;
}
</style>
